import { CreditCard, Link, Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import {
  formatCurrency,
  getCurrencySymbol,
  parseCurrencyInput,
} from "@/utils/format";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link as RouterLink } from "@tanstack/react-router";
import { api } from "@cvx/_generated/api";
import { usePaymentMethods } from "@/hooks";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

export interface PaymentModalData {
  contractId: Id<"creatorContracts">;
  creatorName: string;
  campaignName: string;
  calculatedAmount: number;
  creatorAmount: number;
  platformFee: number;
  currency: "usd" | "eur" | "gbp";
  isManualPaymentOnly?: boolean; // Indicates if creator only has bank details
  creatorBankDetails?: {
    firstName: string;
    lastName: string;
    homeAddress: string;
    iban?: string;
    accountNumber: string;
    bicOrSwift: string;
  } | null;
  creatorInvoiceUrl?: string | null;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContract: PaymentModalData | null;
  paymentDate: string;
  setPaymentDate: (date: string) => void;
  paymentAmount: string;
  setPaymentAmount: (amount: string) => void;
  receiptMode: "file" | "url";
  setReceiptMode: (mode: "file" | "url") => void;
  receiptFile: File | null;
  receiptUrl: string;
  setReceiptUrl: (url: string) => void;
  isUploading: boolean;
  onSubmitPayment: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  minDate?: string;
}

export function PaymentModal({
  isOpen,
  onClose,
  selectedContract,
  paymentDate,
  setPaymentDate,
  paymentAmount,
  setPaymentAmount,
  receiptMode,
  setReceiptMode,
  receiptFile,
  receiptUrl,
  setReceiptUrl,
  isUploading,
  onSubmitPayment,
  onFileChange,
  fileInputRef,
  minDate,
}: PaymentModalProps) {
  // Set initial tab based on whether it's manual payment only
  const initialTab = selectedContract?.isManualPaymentOnly
    ? "manual"
    : "stripe";
  const [activeTab, setActiveTab] = useState<"manual" | "stripe">(initialTab);
  const [isProcessingStripePayment, setIsProcessingStripePayment] =
    useState(false);
  const { toast } = useToast();

  // Update tab when selectedContract changes
  useEffect(() => {
    if (selectedContract?.isManualPaymentOnly) {
      setActiveTab("manual");
    } else {
      setActiveTab("stripe");
    }
  }, [selectedContract?.isManualPaymentOnly]);

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const {
    paymentMethods,
    hasPaymentMethod,
    isLoading: isLoadingPaymentMethods,
    checkPaymentMethods,
    formatCardBrand,
  } = usePaymentMethods();

  const makePaymentAction = useConvexAction(api.stripe.makePayment);

  useEffect(() => {
    if (user?._id && isOpen) {
      checkPaymentMethods(user._id);
    }
  }, [user?._id, isOpen, checkPaymentMethods]);

  const handleStripePayment = async () => {
    if (!selectedContract) return;

    setIsProcessingStripePayment(true);
    try {
      await makePaymentAction({ contractId: selectedContract.contractId });

      toast({
        title: "Payment processed successfully",
        description: `Payment for ${selectedContract.creatorName} has been completed.`,
      });

      onClose();
    } catch (error) {
      console.error("Error processing Stripe payment:", error);
      const errorMessage =
        error instanceof ConvexError ? error.data : "Unknown error occurred";

      toast({
        title: "Payment failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessingStripePayment(false);
    }
  };

  // Validation for manual payments
  const isPaymentAmountValid = () => {
    if (!selectedContract) return false;
    const enteredAmount = parseCurrencyInput(paymentAmount || "0");
    return enteredAmount >= selectedContract.creatorAmount;
  };

  const getPaymentValidationMessage = () => {
    if (!selectedContract) return "";
    const enteredAmount = parseCurrencyInput(paymentAmount || "0");
    if (enteredAmount < selectedContract.creatorAmount) {
      return `Minimum payment amount is ${formatCurrency(selectedContract.creatorAmount, 2, selectedContract.currency)} (creator earnings)`;
    }
    return "";
  };

  const calculatePlatformFeeFromAmount = (amount: number) => {
    return amount * 0.2; // 20% of whatever amount is paid
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isProcessingStripePayment) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Process Payment</DialogTitle>
          <DialogDescription>
            Choose how you want to process this payment.
          </DialogDescription>
        </DialogHeader>

        {selectedContract && (
          <div className="space-y-4 overflow-y-auto flex-1 px-1 scrollbar-hide">
            {/* Contract Info */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Creator:</span>
                <span className="text-sm font-medium">
                  {selectedContract.creatorName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Campaign:</span>
                <span className="text-sm font-medium">
                  {selectedContract.campaignName}
                </span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-primary">
                    Total Amount:
                  </span>
                  <span className="text-sm font-bold text-primary">
                    {formatCurrency(
                      selectedContract.calculatedAmount,
                      2,
                      selectedContract.currency,
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Creator payment:</span>
                  <span className="text-green-600 font-medium">
                    {formatCurrency(
                      selectedContract.creatorAmount,
                      2,
                      selectedContract.currency,
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Platform fee (20%):</span>
                  <span className="text-blue-600 font-medium">
                    {formatCurrency(
                      selectedContract.platformFee,
                      2,
                      selectedContract.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as "manual" | "stripe")
              }
            >
              <TabsList
                className={`grid w-full ${selectedContract?.isManualPaymentOnly ? "grid-cols-1" : "grid-cols-2"}`}
              >
                {!selectedContract?.isManualPaymentOnly && (
                  <TabsTrigger
                    value="stripe"
                    className="flex items-center gap-2"
                  >
                    <CreditCard className="h-4 w-4" />
                    Stripe Payment
                  </TabsTrigger>
                )}
                <TabsTrigger value="manual" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Manual Payment
                </TabsTrigger>
              </TabsList>

              {/* Stripe Payment Tab - only show if not manual payment only */}
              {!selectedContract?.isManualPaymentOnly && (
                <TabsContent value="stripe" className="space-y-4 mt-4">
                  {isLoadingPaymentMethods ? (
                    <div className="bg-muted/50 p-4 rounded-lg text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Loading payment information...
                      </p>
                    </div>
                  ) : hasPaymentMethod && paymentMethods.length > 0 ? (
                    <>
                      {/* Payment Method Display */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-primary">
                          Payment Method:
                        </h4>
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-6 bg-gray-800 rounded text-white text-xs flex items-center justify-center font-bold">
                                {formatCardBrand(paymentMethods[0].brand)}
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  •••• •••• •••• {paymentMethods[0].last4}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Expires{" "}
                                  {paymentMethods[0].expMonth
                                    ?.toString()
                                    .padStart(2, "0")}
                                  /{paymentMethods[0].expYear}
                                </p>
                              </div>
                            </div>
                            <RouterLink
                              to="/dashboard/settings/billing"
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              Change
                            </RouterLink>
                          </div>
                        </div>
                      </div>

                      {/* Payment Info */}
                      <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg space-y-2">
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
                          💳 Automatic Payment
                        </h4>
                        <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                          <p>
                            • Payment will be charged to your card automatically
                          </p>
                          <p>
                            • Funds will be transferred directly to the creator
                          </p>
                          <p>
                            • A receipt will be automatically uploaded to the
                            contract
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-amber-50 dark:bg-amber-950/20 p-6 rounded-lg text-center space-y-4">
                      <div className="text-amber-800 dark:text-amber-200">
                        <div className="text-2xl mb-2">💳</div>
                        <h4 className="text-lg font-medium mb-2">
                          Payment Method Required
                        </h4>
                        <p className="text-sm mb-4">
                          You need to add a payment method to your account
                          before you can process automatic payments.
                        </p>
                        <RouterLink
                          to="/dashboard/settings/billing"
                          className="inline-flex items-center justify-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium transition-colors"
                        >
                          Add Payment Method
                        </RouterLink>
                      </div>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Manual Payment Tab */}
              <TabsContent value="manual" className="space-y-4 mt-4">
                {/* Check if payment method is available for manual payments */}
                {isLoadingPaymentMethods ? (
                  <div className="bg-muted/50 p-4 rounded-lg text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Loading payment information...
                    </p>
                  </div>
                ) : !hasPaymentMethod || paymentMethods.length === 0 ? (
                  <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-lg text-center space-y-4">
                    <div className="text-red-800 dark:text-red-200">
                      <div className="text-2xl mb-2">💳</div>
                      <h4 className="text-lg font-medium mb-2">
                        Payment Method Required for Manual Payments
                      </h4>
                      <p className="text-sm mb-4">
                        You need to add a payment method to your account before
                        you can process manual payments.
                      </p>
                      <RouterLink
                        to="/dashboard/settings/billing"
                        className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
                      >
                        Add Payment Method
                      </RouterLink>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Payment Method Display for Manual Payments */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-primary">
                        Platform Fee Payment Method:
                      </h4>
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-6 bg-gray-800 rounded text-white text-xs flex items-center justify-center font-bold">
                              {formatCardBrand(paymentMethods[0].brand)}
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                •••• •••• •••• {paymentMethods[0].last4}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Expires{" "}
                                {paymentMethods[0].expMonth
                                  ?.toString()
                                  .padStart(2, "0")}
                                /{paymentMethods[0].expYear}
                              </p>
                            </div>
                          </div>
                          <RouterLink
                            to="/dashboard/settings/billing"
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Change
                          </RouterLink>
                        </div>
                      </div>
                    </div>

                    {/* Platform Fee Explanation for Manual Payments */}
                    <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg space-y-2">
                      <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100 flex items-center gap-2">
                        🏦 Manual Payment Process
                      </h4>
                      <div className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                        <p>
                          <strong>Step 1:</strong> Transfer the payment amount
                          to the creator using their bank details below.
                        </p>
                        <p>
                          <strong>Step 2:</strong> Record the payment here with
                          a receipt.
                        </p>
                        <p>
                          <strong>Step 3:</strong> We'll automatically charge
                          your card on file{" "}
                          <span className="font-bold text-blue-700 dark:text-blue-300">
                            20% of whatever amount you pay
                          </span>{" "}
                          as our platform fee.
                        </p>
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded text-xs">
                          <strong>Example:</strong> If you pay{" "}
                          {formatCurrency(
                            selectedContract?.creatorAmount || 0,
                            2,
                            selectedContract?.currency || "usd",
                          )}
                          , we charge{" "}
                          {formatCurrency(
                            calculatePlatformFeeFromAmount(
                              selectedContract?.creatorAmount || 0,
                            ),
                            2,
                            selectedContract?.currency || "usd",
                          )}{" "}
                          fee. If you pay more, the fee increases
                          proportionally.
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Creator Bank Details - only show if payment method is available */}
                {hasPaymentMethod &&
                  !isLoadingPaymentMethods &&
                  selectedContract?.creatorBankDetails && (
                    <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                      <h4 className="text-sm font-medium text-primary flex items-center gap-2">
                        🏦 Creator Bank Details
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">
                            Account Holder
                          </p>
                          <p className="font-medium">
                            {selectedContract.creatorBankDetails.firstName}{" "}
                            {selectedContract.creatorBankDetails.lastName}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">
                            Address
                          </p>
                          <p className="font-medium">
                            {selectedContract.creatorBankDetails.homeAddress}
                          </p>
                        </div>
                        <div>
                          {selectedContract.creatorBankDetails.iban && (
                            <>
                              <p className="text-muted-foreground text-xs">
                                IBAN
                              </p>
                              <p className="font-mono text-sm">
                                {selectedContract.creatorBankDetails.iban}
                              </p>
                            </>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">
                            Account Number
                          </p>
                          <p className="font-mono text-sm">
                            {selectedContract.creatorBankDetails.accountNumber}
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-muted-foreground text-xs">
                            BIC/SWIFT Code
                          </p>
                          <p className="font-mono text-sm">
                            {selectedContract.creatorBankDetails.bicOrSwift}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Creator Invoice - only show if payment method is available */}
                {hasPaymentMethod &&
                  !isLoadingPaymentMethods &&
                  selectedContract?.creatorInvoiceUrl && (
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-primary flex items-center gap-2 mb-2">
                        📄 Creator Invoice
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="w-full"
                      >
                        <a
                          href={selectedContract.creatorInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2"
                        >
                          <Upload className="h-4 w-4" />
                          View/Download Invoice
                        </a>
                      </Button>
                    </div>
                  )}

                {/* Payment form fields - only show if payment method is available */}
                {hasPaymentMethod && !isLoadingPaymentMethods && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="payment-date">Payment Date</Label>
                      <Input
                        id="payment-date"
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        min={minDate}
                        max={new Date().toISOString().split("T")[0]}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Payment date must be between the contract start date and
                        today
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="payment-amount">
                        Amount Paid ({selectedContract.currency.toUpperCase()})
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                          {getCurrencySymbol(selectedContract.currency)}
                        </span>
                        <Input
                          id="payment-amount"
                          type="text"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="0.00"
                          className="pl-8"
                          required
                        />
                      </div>
                      <div className="text-xs space-y-1">
                        <p className="text-muted-foreground">
                          Minimum amount (creator earnings):{" "}
                          {formatCurrency(
                            selectedContract.creatorAmount,
                            2,
                            selectedContract.currency,
                          )}
                        </p>
                        {paymentAmount &&
                          parseCurrencyInput(paymentAmount) > 0 && (
                            <div className="bg-blue-50 dark:bg-blue-950/20 p-2 rounded">
                              <p className="text-blue-800 dark:text-blue-200 font-medium">
                                Platform fee (20%):{" "}
                                {formatCurrency(
                                  calculatePlatformFeeFromAmount(
                                    parseCurrencyInput(paymentAmount || "0"),
                                  ),
                                  2,
                                  selectedContract.currency,
                                )}
                              </p>
                              <p className="text-blue-700 dark:text-blue-300 text-xs">
                                This will be charged to your card after
                                recording payment
                              </p>
                            </div>
                          )}
                        {!isPaymentAmountValid() && paymentAmount && (
                          <p className="text-red-600 font-medium">
                            {getPaymentValidationMessage()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Receipt Mode Toggle */}
                    <div className="space-y-2">
                      <Label>Receipt</Label>
                      <div className="flex rounded-lg border border-input p-1">
                        <button
                          type="button"
                          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            receiptMode === "file"
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => setReceiptMode("file")}
                        >
                          <Upload className="h-4 w-4 mr-2 inline" />
                          Upload File
                        </button>
                        <button
                          type="button"
                          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            receiptMode === "url"
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          onClick={() => setReceiptMode("url")}
                        >
                          <Link className="h-4 w-4 mr-2 inline" />
                          Receipt URL
                        </button>
                      </div>
                    </div>

                    {receiptMode === "file" ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf"
                            onChange={onFileChange}
                            className="flex-1"
                          />
                          {receiptFile && (
                            <div className="flex items-center gap-1 text-sm text-green-600">
                              <Upload className="h-4 w-4" />
                              {receiptFile.name}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Upload an image or PDF of the payment receipt
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                          <Link className="h-4 w-4 text-muted-foreground" />
                          <input
                            type="url"
                            value={receiptUrl}
                            onChange={(e) => setReceiptUrl(e.target.value)}
                            placeholder="https://example.com/receipt.pdf"
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Provide a URL to the payment receipt
                        </p>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="flex gap-2 flex-shrink-0 mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessingStripePayment}
          >
            Cancel
          </Button>

          {activeTab === "stripe" && !selectedContract?.isManualPaymentOnly ? (
            hasPaymentMethod ? (
              <Button
                onClick={handleStripePayment}
                disabled={isLoadingPaymentMethods || isProcessingStripePayment}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isProcessingStripePayment ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Process Payment
                  </>
                )}
              </Button>
            ) : (
              <RouterLink to="/dashboard/settings/billing">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Add Payment Method
                </Button>
              </RouterLink>
            )
          ) : hasPaymentMethod ? (
            <Button
              onClick={onSubmitPayment}
              disabled={
                !paymentDate ||
                !paymentAmount ||
                !isPaymentAmountValid() ||
                isUploading ||
                (receiptMode === "file" && !receiptFile) ||
                (receiptMode === "url" && !receiptUrl.trim()) ||
                isProcessingStripePayment ||
                isLoadingPaymentMethods
              }
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {receiptMode === "file" ? "Uploading..." : "Recording..."}
                </>
              ) : (
                "Record Manual Payment"
              )}
            </Button>
          ) : (
            <RouterLink to="/dashboard/settings/billing">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Add Payment Method
              </Button>
            </RouterLink>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
